import * as Sentry from "@sentry/react";

/**
 * Sentry Error Monitoring Configuration
 *
 * To enable Sentry:
 * 1. Create a Sentry project at https://sentry.io
 * 2. Get your DSN from Project Settings > Client Keys
 * 3. Set REACT_APP_SENTRY_DSN environment variable
 *
 * Features enabled:
 * - Automatic error capturing
 * - Browser tracing for performance
 * - Session replay for debugging
 */

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

export const initSentry = (): void => {
  // Only initialize in production or if DSN is explicitly set
  if (!SENTRY_DSN) {
    if (process.env.NODE_ENV === "production") {
      console.warn("Sentry DSN not configured. Error monitoring disabled.");
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV,

    // Performance monitoring
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,

    // Session replay (captures user interactions before an error)
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Filter out noisy errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Ignore network errors (user connectivity issues)
      if (error instanceof TypeError && error.message?.includes("Failed to fetch")) {
        return null;
      }

      // Ignore chunk loading errors (usually due to deployments)
      if (error instanceof Error && error.message?.includes("Loading chunk")) {
        return null;
      }

      return event;
    },

    // Don't send PII
    sendDefaultPii: false,
  });

  console.log("Sentry initialized");
};

// Error boundary wrapper for React components
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Manual error capturing
export const captureException = Sentry.captureException;
export const captureMessage = Sentry.captureMessage;

// Set user context (call after login)
export const setUser = (user: { id?: string | number; email?: string; username?: string } | null): void => {
  Sentry.setUser(user);
};

// Add breadcrumb for debugging
export const addBreadcrumb = Sentry.addBreadcrumb;

// Performance monitoring
export const startTransaction = (name: string, op: string) => {
  return Sentry.startInactiveSpan({ name, op });
};

export default Sentry;
